import { TApiSDKOptions } from '../main';

type TTokens = {
  accessToken: string;
  refreshToken: string;
};

type TAuthClient = {
  login: (username: string, password: string) => Promise<TTokens | undefined>;
  logout: (accessToken: string) => Promise<void>;
  refresh(refreshToken: string): Promise<TTokens | undefined>;
  forgotPassword(email: string): Promise<void>;
  resetPassword(email: string, currentPassword: string, newPassword: string, confirmPassword: string): Promise<void>;
};

type TDeviceInfoService = {
  /**
   * Optional method to detect incognito/private mode in the browser
   * If not provided, incognito mode will be assumed as false
   * @return {Promise<boolean>} true if in incognito/private mode, false otherwise
   */
  getIncognitoMode?(): Promise<boolean>;
  /**
   * Method to get a unique fingerprint ID for the device/browser
   * @return {Promise<string>} Unique fingerprint ID (length: 64 characters)
   */
  getFingerprintId(): Promise<string>;
  /**
   * Method to get a unique Poseidon ID for the device/browser
   * @return {string} Unique Poseidon ID (length: 64 characters)
   */
  getPoseidonId(): string;
};

function AuthClient(options: TApiSDKOptions, deviceInfoService: TDeviceInfoService): TAuthClient {
  return {
    async login(username: string, password: string): Promise<TTokens | undefined> {
      const isPrivate = deviceInfoService.getIncognitoMode ? await deviceInfoService.getIncognitoMode() : false;
      const fingerprintId = await deviceInfoService.getFingerprintId();
      const poseidonId = deviceInfoService.getPoseidonId();

      const response = await fetch(`${options.host}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Incognito-Mode': isPrivate.toString(),
          'X-Fingerprint-Id': fingerprintId,
          'X-Poseidon-Id': poseidonId,
        },
        body: JSON.stringify({
          grant_type: 'password',
          client_id: options.clientId,
          client_secret: options.clientSecret,
          username,
          password,
        }),
      });

      if (response.status !== 200) {
        console.error('AuthClient.token failed', response);
        return;
      }

      const data = await response.json();
      const { access_token, refresh_token } = data;

      return {
        accessToken: access_token,
        refreshToken: refresh_token,
      };
    },

    async logout(accessToken: string): Promise<void> {
      try {
        const response = await fetch(`${options.host}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({}),
        });

        if (response.status === 204) {
          console.log('%c✔ Successfully logged out', 'color:#4cca9f');
        } else {
          // Logout failed
          console.error('Logout failed', response);
        }
      } catch (_error) {
        // Error during logout
      }
    },

    async refresh(refreshToken: string): Promise<TTokens | undefined> {
      const isPrivate = deviceInfoService.getIncognitoMode ? await deviceInfoService.getIncognitoMode() : false;
      const fingerprintId = await deviceInfoService.getFingerprintId();
      const poseidonId = deviceInfoService.getPoseidonId();

      console.log(`isPrivate: ${isPrivate}`);
      console.log(`fingerprintId: ${fingerprintId}`);
      console.log(`poseidonId: ${poseidonId}`);

      console.log(`clientId: ${options.clientId}`);
      console.log(`clientSecret: ${options.clientSecret}`);
      console.log(`refreshToken: ${refreshToken}`);

      const response = await fetch(`${options.host}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Incognito-Mode': isPrivate.toString(),
          'X-Fingerprint-Id': fingerprintId,
          'X-Poseidon-Id': poseidonId,
        },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          client_id: options.clientId,
          client_secret: options.clientSecret,
          refresh_token: refreshToken,
        }),
      });

      if (response.status !== 200) {
        console.error('AuthClient.refresh failed', response);
        return;
      }

      const data = await response.json();
      const { access_token, refresh_token } = data;

      return {
        accessToken: access_token,
        refreshToken: refresh_token,
      };
    },

    async forgotPassword(email: string): Promise<void> {
      const response = await fetch(`${options.host}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      });

      if (response.status !== 204) {
        console.error('AuthClient.forgotPassword failed', response);
        return;
      }
    },

    async resetPassword(
      email: string,
      currentPassword: string,
      newPassword: string,
      confirmPassword: string
    ): Promise<void> {
      const response = await fetch(`${options.host}/auth/reset-password/${email}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });

      if (response.status !== 204) {
        console.error('AuthClient.resetPassword failed', response);
        return;
      }
    },
  };
}

export { AuthClient };
export type { TAuthClient, TDeviceInfoService };
